import { GetServerSideProps } from 'next';
import CustomError from '@screens/CustomError';
import { ProductJsonLd, NextSeo, ProductJsonLdProps } from 'next-seo';
import getConfig from 'next/config';
import {
  Channel,
  EVENT_SOURCE,
  FIRST_ORDER_GIFT,
  PRODUCT_PAGE_ATTRIBUTES_TO_RETRIEVE,
} from '@monofront/business/constants';
import { IBreadcrumb, IProduct } from '@monofront/types';
import { IPost } from '@monofront/types';
import { useEffect } from 'react';
import aa from 'search-insights';
import { productService } from '@services/businessContainer';
import {
  formatProductToKlaviyo as createFormatProductEcommerceToKlaviyo,
  formatSearchProductToTracking,
  getChannelFromLocale,
  isoDateSort,
  format,
} from '@monofront/business/utils';
import { useGetProductReviews } from '@monofront/business/hooks';
import {
  klaviyoTrackEvent,
  klaviyoTrackViewedItem,
  pushDataLayer,
} from '@utils/tracking';
import { trackEvent } from '@monofront/tracking';
import useI18n from '@hooks/i18n/useI18n';
import { OneTrustCookieCategories } from '@components/Vendors/Vendors';
import { LFPage, injectLayoutData } from '@utils/layout';
import { useCacheFirstAuthentication } from '@context/persistedAuthContext';
import getCookieConsent from '@utils/getCookieConsent';
import { injectGetServerSidePropsHeaders } from '@utils/query';
import Product from '@screens/Product';
import { ProductPageContextProvider } from '@context/ProductPageContext';
import { isMobileApp } from '@utils/environment';
import { DeliveryBarCTA } from '@features/DeliveryBar';
import { useFormatProductWeight } from '@monofront/business/hooks/product/useFormatProductWeight';
import { useTranslations } from 'next-intl';

const {
  publicRuntimeConfig: { appBaseUrl },
} = getConfig();

const formatProductEcommerceToKlaviyo =
  createFormatProductEcommerceToKlaviyo(appBaseUrl);

interface IPageProduct {
  key?: string;
  product?: IProduct;
  tips?: IPost[];
  channel?: Channel;
}

const PageProduct: LFPage<IPageProduct> = ({ product, tips, channel }) => {
  const { customer } = useCacheFirstAuthentication();
  const { currency } = useI18n();
  const useGetProductReviewsHook = useGetProductReviews(5);
  const { productReviews, getProductReviews } = useGetProductReviewsHook;
  const { formatProductWeight } = useFormatProductWeight();
  const tProducts = useTranslations('Products');

  const formattedProductWeight = formatProductWeight(
    product?.meta?.finance,
    false,
  );

  useEffect(() => {
    if (product) {
      const klaviyoItem = formatProductEcommerceToKlaviyo(product);

      klaviyoTrackEvent({
        name: 'Viewed Product',
        data: {
          eventSource: isMobileApp ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
          ...klaviyoItem,
        },
      });
      klaviyoTrackViewedItem({
        Title: klaviyoItem.ProductName,
        ItemId: klaviyoItem.ProductID,
        Categories: klaviyoItem.ProductCategories,
        ImageUrl: klaviyoItem.ImageURL,
        Url: klaviyoItem.ProductURL,
        Metadata: {
          Brand: klaviyoItem.Brand,
          Price: klaviyoItem.ItemPrice,
          CompareAtPrice: klaviyoItem.CompareAtPrice,
        },
      });

      // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
      pushDataLayer({
        ecommerce: {
          detail: {
            products: [
              {
                name: `${product.title}`,
                id: `${product.sku}`,
                price: `${product.price}`,
                brand: `${product.vendor}`,
                category: `${product.product_type}`,
                dimension6: `${product.inventory_quantity}`,
              },
            ],
          },
        },
      });

      trackEvent({
        type: 'LFViewItem',
        data: {
          products: [{ ...formatSearchProductToTracking(product, 1) }],
          currency,
        },
      });
    }
  }, [product]);

  useEffect(() => {
    if (product) {
      getProductReviews(product.sku);
    }
  }, [product]);

  useEffect(() => {
    // we don't wait for the user to be firebase connected anymore, we assume that the persisted value could be false
    aa('viewedObjectIDs', {
      index: productService.getProductPageIndex(channel),
      eventName: 'Product Viewed',
      objectIDs: [`${product?.objectID}`],
      // If the user consents to functionality cookies
      ...(getCookieConsent(OneTrustCookieCategories.FUNCTIONALITY)
        ? { userToken: customer?.uid }
        : {}),
    });
  }, []);

  if (!product) {
    return <CustomError statusCode={404} />;
  }

  const latestReview = [...productReviews]
    .sort(isoDateSort('createdAt'))
    .reverse()[0];

  const seoTitle = product.meta?.seo?.title || product.title;
  const seoDescription = tProducts('seoDescription', {
    product: product.title,
    brand: product.vendor,
    weight: formattedProductWeight,
    price: format(product.price),
  });

  const jsonLD: ProductJsonLdProps = {
    productName: seoTitle,
    brand: product.vendor,
    images: [product.product_image!],
    description: seoDescription,
    sku: product.sku,
    mpn: product.barcode,
    offers: [
      {
        priceCurrency: 'EUR',
        price: String(product.price),
        priceValidUntil: new Date(
          new Date().setFullYear(new Date().getFullYear() + 1),
        ).toISOString(),
        availability: product.inventory_available
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        seller: { name: 'La Fourche' },
        url: `${appBaseUrl}/products/${product.handle}`,
      },
    ],
    ...(product.meta?.reviews_stats?.global.average_rating &&
      product.meta?.reviews_stats?.global.count && {
        aggregateRating: {
          ratingValue: String(product.meta.reviews_stats.global.average_rating),
          reviewCount: String(product.meta.reviews_stats.global.count),
        },
      }),
    reviews: latestReview
      ? {
          author: latestReview.author.publicDisplayName,
          datePublished: latestReview.createdAt,
          reviewRating: {
            ratingValue: `${latestReview.rating}`,
            bestRating: '5',
          },
        }
      : undefined,
  };

  return (
    <>
      <NextSeo
        title={seoTitle}
        description={seoDescription}
        canonical={`${appBaseUrl}/products/${product.handle}`}
        openGraph={{
          url: `${appBaseUrl}/products/${product.handle}`,
          title: seoTitle,
          description: seoDescription,
          images: [
            {
              url: product.product_image!,
            },
          ],
        }}
      />
      <ProductJsonLd {...jsonLD} />
      <div className="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-px-3 desktop:tw-mx-auto desktop:tw-min-w-[500px] desktop:tw-max-w-[500px] desktop:tw-p-0">
        <DeliveryBarCTA />
      </div>
      <ProductPageContextProvider
        useGetProductReviewsHook={useGetProductReviewsHook}
      >
        <Product product={product} tips={tips} />
      </ProductPageContextProvider>
    </>
  );
};

export const getServerSideProps = injectGetServerSidePropsHeaders<IPageProduct>(
  injectLayoutData<IPageProduct, GetServerSideProps>(
    async ({ params, locale }) => {
      try {
        const channel = getChannelFromLocale(locale);

        const { product, tips } = await productService.getProductWithTips(
          params?.handle as string,
          8,
          channel,
          PRODUCT_PAGE_ATTRIBUTES_TO_RETRIEVE,
        );

        if (product?.tags?.some((tag) => tag === FIRST_ORDER_GIFT)) {
          return {
            notFound: true,
          };
        }

        if (!product) {
          const unpublishedProduct = await productService.getProductByHandle(
            params?.handle as string,
            channel,
          );

          if (!unpublishedProduct) {
            return {
              notFound: true,
            };
          }

          const metafieldBreadcrumb = unpublishedProduct.metafields.find(
            (metafield) => metafield.key === 'breadcrumb',
          );

          if (!metafieldBreadcrumb) {
            return {
              notFound: true,
            };
          }

          const breadcrumb: IBreadcrumb[] = JSON.parse(
            metafieldBreadcrumb.value,
          );

          const lastBreadcrumbEntry = breadcrumb.at(-1)?.handle ?? '';

          return {
            redirect: {
              destination: `/collections/${lastBreadcrumbEntry}`,
              permanent: true,
            },
          };
        }

        return {
          props: {
            key: params?.handle as string,
            product,
            tips: tips?.length !== 0 ? tips : null,
            channel,
          },
        };
      } catch (error: any) {
        return {
          notFound: true,
        };
      }
    },
  ),
);

export default PageProduct;
